/**
 * BoSecretariatElevesPage — Liste + CRUD basique des fiches élèves du secrétariat.
 * Route : /bo/secretariat/eleves
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Users, Search, AlertCircle, X } from 'lucide-react';
import api from '../../api';

const BO_BLUE = '#1d4ed8';
const FORMULE_LABEL = {
  permis_b: 'Permis B', aac: 'AAC (accompagnée)', permis_b78: 'B78 (BEA)',
  permis_a1: 'A1 · 125 cm³', permis_a2: 'A2 · Moto', passerelle_a: 'Passerelle A2→A',
  formation_125: 'Formation 125', code_seul: 'Code seul',
};

export default function BoSecretariatElevesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', formula: 'permis_b' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'Élèves · Back-office';
    reload();
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.boListEleves();
      setItems(r.items || []);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        navigate('/login', { state: { from: '/bo/secretariat/eleves' } });
        return;
      }
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.boCreateEleve({ ...form, name: form.name.trim() });
      setShowAdd(false);
      setForm({ name: '', email: '', phone: '', formula: 'permis_b' });
      reload();
    } catch (e) {
      setError(e.message || 'Création impossible');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Supprimer la fiche de "${row.name}" ? (leçons associées supprimées aussi)`)) return;
    try {
      await api.boDeleteEleve(row.id);
      reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const filtered = items.filter((i) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (i.name || '').toLowerCase().includes(s) || (i.email || '').toLowerCase().includes(s);
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="bo-eleves">
      <Link to="/bo" className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/60 hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back-office
      </Link>

      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.22em] mb-1" style={{ color: BO_BLUE }}>
            Secrétariat
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Élèves ({items.length})
          </h1>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-black shadow-sm hover:opacity-90 transition"
          style={{ background: BO_BLUE }}
          data-testid="btn-add-eleve"
        >
          <Plus className="w-4 h-4" /> Nouvel élève
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un élève (nom, email)…"
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border-2 border-border bg-card text-sm outline-none focus:border-[#1d4ed8] transition"
          data-testid="search-eleves"
        />
      </div>

      {error && (
        <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-3 mb-4 flex items-start gap-2 text-xs" data-testid="bo-eleves-error">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
          <div className="text-foreground/80">{error}</div>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-sm text-foreground/50 py-12 text-center">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-10 text-center">
          <Users className="w-8 h-8 mx-auto mb-3 text-foreground/40" />
          <p className="text-sm text-foreground/60">
            {items.length === 0
              ? 'Aucune fiche élève pour le moment. Ajoutez-en une ou lancez le "seed démo".'
              : 'Aucun résultat pour cette recherche.'}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border-2 border-border overflow-hidden">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-muted/50 text-foreground/60 uppercase text-[10px] font-black tracking-widest">
              <tr>
                <th className="text-left px-3 sm:px-4 py-2.5">Nom</th>
                <th className="text-left px-3 sm:px-4 py-2.5 hidden sm:table-cell">Email</th>
                <th className="text-left px-3 sm:px-4 py-2.5">Formule</th>
                <th className="text-right px-3 sm:px-4 py-2.5">Heures</th>
                <th className="text-right px-3 sm:px-4 py-2.5 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-border/60 hover:bg-muted/30 transition"
                  data-testid={`row-eleve-${r.id}`}
                >
                  <td className="px-3 sm:px-4 py-2.5 font-black text-foreground">{r.name}</td>
                  <td className="px-3 sm:px-4 py-2.5 text-foreground/70 hidden sm:table-cell">{r.email || '—'}</td>
                  <td className="px-3 sm:px-4 py-2.5 text-foreground/70">
                    <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
                          style={{ background: 'rgba(29,78,216,0.1)', color: BO_BLUE }}>
                      {FORMULE_LABEL[r.formula] || r.formula}
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 text-right text-foreground/80 font-mono text-[11px]">
                    {r.hours_done}/{r.hours_package || '?'}
                  </td>
                  <td className="px-3 sm:px-4 py-2.5 text-right">
                    <button
                      onClick={() => handleDelete(r)}
                      className="p-1.5 rounded hover:bg-red-500/10 text-red-600 transition"
                      title="Supprimer"
                      data-testid={`btn-delete-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Add */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <form
            onSubmit={handleAdd}
            onClick={(e) => e.stopPropagation()}
            className="bg-card rounded-2xl border-2 border-border max-w-md w-full p-5 sm:p-6 space-y-4"
            data-testid="modal-add-eleve"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Nouvel élève
              </h2>
              <button type="button" onClick={() => setShowAdd(false)}>
                <X className="w-5 h-5 text-foreground/60" />
              </button>
            </div>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Nom complet *</span>
              <input
                autoFocus required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                data-testid="input-eleve-name"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                data-testid="input-eleve-email"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Téléphone</span>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                data-testid="input-eleve-phone"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Formule</span>
              <select
                value={form.formula}
                onChange={(e) => setForm({ ...form, formula: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                data-testid="input-eleve-formule"
              >
                {Object.entries(FORMULE_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </label>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="flex-1 px-3 py-2 rounded-lg border-2 border-border text-sm font-black hover:bg-muted"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-3 py-2 rounded-lg text-white text-sm font-black hover:opacity-90 disabled:opacity-50"
                style={{ background: BO_BLUE }}
                data-testid="btn-submit-eleve"
              >
                {saving ? 'Création…' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
